import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { listOrgs } from "../api/orgs.js";
import { listTenants } from "../api/tenants.js";
import { listSubTenants } from "../api/subtenants.js";
import { setCurrentSubTenant } from "../api/client.js";

const HierarchyContext = createContext(null);

const STORAGE_PREFIX = "mat.hierarchy";

function loadSaved(key, fallback) {
  try { return localStorage.getItem(`${STORAGE_PREFIX}.${key}`) || fallback; }
  catch { return fallback; }
}

function save(key, value) {
  try { localStorage.setItem(`${STORAGE_PREFIX}.${key}`, value || ""); } catch {}
}

export function HierarchyProvider({ children }) {
  const [orgs, setOrgs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [subTenants, setSubTenants] = useState([]);
  const [orgId, setOrgIdState] = useState(() => loadSaved("orgId", ""));
  const [tenantId, setTenantIdState] = useState(() => loadSaved("tenantId", ""));
  const [subTenantId, setSubTenantIdState] = useState(() => loadSaved("subTenantId", ""));
  const [loading, setLoading] = useState(true);

  // Sync sub-tenant to the API client on every change
  useEffect(() => {
    setCurrentSubTenant(subTenantId || null);
  }, [subTenantId]);

  // Load orgs on mount
  useEffect(() => {
    listOrgs()
      .then((list) => {
        setOrgs(list);
        if (list.length > 0 && !list.find((o) => o._id === orgId)) {
          setOrgId(list[0]._id);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load tenants when org changes
  useEffect(() => {
    if (!orgId) { setTenants([]); return; }
    listTenants(orgId)
      .then((list) => {
        setTenants(list);
        if (list.length > 0 && !list.find((t) => t._id === tenantId)) {
          setTenantId(list[0]._id);
        } else if (list.length === 0) {
          setTenantId("");
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Load sub-tenants when tenant changes
  useEffect(() => {
    if (!orgId || !tenantId) { setSubTenants([]); return; }
    listSubTenants(orgId, tenantId)
      .then((list) => {
        setSubTenants(list);
        if (list.length > 0 && !list.find((s) => s._id === subTenantId)) {
          setSubTenantId(list[0]._id);
        } else if (list.length === 0) {
          setSubTenantId("");
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, tenantId]);

  const setOrgId = useCallback((id) => {
    setOrgIdState(id);
    save("orgId", id);
    // Reset downstream
    setTenantIdState("");
    save("tenantId", "");
    setSubTenantIdState("");
    save("subTenantId", "");
  }, []);

  const setTenantId = useCallback((id) => {
    setTenantIdState(id);
    save("tenantId", id);
    // Reset downstream
    setSubTenantIdState("");
    save("subTenantId", "");
  }, []);

  const setSubTenantId = useCallback((id) => {
    setSubTenantIdState(id);
    save("subTenantId", id);
  }, []);

  const currentOrg = orgs.find((o) => o._id === orgId) || null;
  const currentTenant = tenants.find((t) => t._id === tenantId) || null;
  const currentSubTenant = subTenants.find((s) => s._id === subTenantId) || null;

  return (
    <HierarchyContext.Provider value={{
      orgs, tenants, subTenants,
      orgId, tenantId, subTenantId,
      setOrgId, setTenantId, setSubTenantId,
      currentOrg, currentTenant, currentSubTenant,
      loading,
      // Convenience for refreshing after admin CRUD
      refreshOrgs: () => listOrgs().then(setOrgs).catch(console.error),
      refreshTenants: () => orgId ? listTenants(orgId).then(setTenants).catch(console.error) : Promise.resolve(),
      refreshSubTenants: () => orgId && tenantId ? listSubTenants(orgId, tenantId).then(setSubTenants).catch(console.error) : Promise.resolve(),
    }}>
      {children}
    </HierarchyContext.Provider>
  );
}

export function useHierarchy() {
  const ctx = useContext(HierarchyContext);
  if (!ctx) throw new Error("useHierarchy must be inside <HierarchyProvider>");
  return ctx;
}
